/**
 * Budget & Draws tab — construction capital, Scope-of-Work workbook, Draws.
 *
 * Backend: lendmarq-os frontend_documentation/budget-scope-of-work-draws.md.
 * Construction loans only (Fix and Flip / Ground Up Construction). Both pipelines
 * expose the same endpoints under their own prefix, so useBudgetDrawsApi binds to
 * the active pipeline via PipelineContext and this one component serves both.
 *
 * The nav entry is gated on the construction product in LoanDrilldownScreen; this
 * tab additionally trusts `budgetTabEligible` from the GETs as the authority.
 *
 * Layout follows the redesign rationale: the whole tab is one formula
 * (eligible = %complete × holdback − drawn; gross = min(requested, eligible);
 * net = gross − fees), and the sections are ordered so it reads top to bottom —
 * capital position, then the budget that is the cost basis, then the releases.
 * See docs/budget-and-draws-redesign-rationale.md.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanTabCanvas from "../../shared/tabLayout/LoanTabCanvas";
import { useBudgetDrawsApi } from "../../../../api/useBudgetDrawsApi";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { sidebarOpen } from "../../../../redux/reducers/sidebarReducer";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import DrilldownSubTabStrip from "../../components/DrilldownSubTabStrip";
import {
  BUDGET_SECTION_ANCHOR_SX,
  BUDGET_SECTION_PARAM,
  BUDGET_SECTIONS,
  budgetSectionAnchor,
  getBudgetDrawsSections,
} from "./budgetDrawsSections";
import CapitalOverviewCard from "./CapitalOverviewCard";
import FeasibilitySection from "./FeasibilitySection";
import InspectionPhotosSection from "./InspectionPhotosSection";
import ScopeOfWorkWorkbook from "./ScopeOfWorkWorkbook";
import DrawsSection from "./DrawsSection";
import InspectionsPanel from "./InspectionsPanel";
import { photosByLineItem } from "./reportPhotosModel";
import { isInspectionBackedDraw } from "./newDrawFields";
import { T, buildEligible, num } from "./budgetDrawsTheme";

const BudgetDrawsTab = ({ value, tab }) => {
  const api = useBudgetDrawsApi();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;
  const properties = loan?.subjectProperties;

  const [sow, setSow] = useState(null); // { scopes, budgetTabEligible }
  const [drawsData, setDrawsData] = useState(null); // { draws, rollup }
  const [loading, setLoading] = useState(true);
  const [savingSheet, setSavingSheet] = useState(false);
  // Live rows from the workbook draft, so the capital band's budget-vs-holdback
  // check and the draws strip's completion basis track what's on screen.
  const [sheetRows, setSheetRows] = useState([]);
  // ReportPhotoReview[] for the deal, newest report first.
  const [reviews, setReviews] = useState([]);
  // A budget line the user asked to see from a photo / evidence chip. The
  // workbook scrolls to it and flashes it, then clears the request.
  const [focusLineId, setFocusLineId] = useState(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      // Independent surfaces — one failing must not blank the other.
      const [sowRes, drawsRes, photoRes] = await Promise.all([
        api.getScopeOfWork(dealId).catch(() => null),
        api.getDraws(dealId).catch(() => null),
        // Report photo reviews feed THREE surfaces (the feasibility section, the
        // inspections section and the workbook's per-line camera), so the one
        // call per deal lives here rather than in each of them.
        api.getReportPhotoReviews(dealId).catch(() => null),
      ]);
      setSow(sowRes);
      setDrawsData(drawsRes);
      setReviews(Array.isArray(photoRes?.reviews) ? photoRes.reviews : []);
    } finally {
      setLoading(false);
    }
  }, [api, dealId]);

  useEffect(() => {
    load();
  }, [load]);

  // Swap one re-serialized sheet into the list rather than refetching the tab.
  const replaceSheet = useCallback((res) => {
    if (!res) return;
    setSow((prev) => {
      if (!prev?.scopes) return { ...(prev || {}), scopes: [res] };
      const key = (s) => String(s?.subjectPropertyId || s?._id || s?.oid || "");
      const scopes = prev.scopes.map((s) => (key(s) === key(res) ? res : s));
      return { ...prev, scopes };
    });
  }, []);

  const handleSaveSheet = useCallback(
    async (subjectPropertyId, body) => {
      if (!dealId) return;
      setSavingSheet(true);
      try {
        const res = await api.saveScopeOfWork(dealId, body);
        // The PUT response is the re-serialized sheet (computed fields included).
        replaceSheet(res);
        enqueueSnackbar("Scope of work saved", { variant: "success" });
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail ||
            err?.message ||
            "Could not save the sheet.",
          { variant: "error" },
        );
      } finally {
        setSavingSheet(false);
      }
    },
    [api, dealId, enqueueSnackbar, replaceSheet],
  );

  /**
   * Rebuild sheet(s) from the loan's extracted documents.
   *
   * `refresh` is decided BEFORE the request by RebuildSheetDialog — the operator
   * consents to the overwrite up front. (The previous flow POSTed without it and
   * tried to detect a skip in the response, reading `res.scopes` where the
   * backend returns `res.results`; the prompt therefore never fired.) We still
   * read `results` here so a genuinely skipped sheet is reported honestly rather
   * than toasting success over a no-op.
   */
  const handleRebuild = useCallback(
    async (refresh) => {
      if (!dealId) return;
      setSavingSheet(true);
      try {
        const res = await api.populateScopeOfWork(dealId, { refresh });
        const results = Array.isArray(res?.results) ? res.results : [];
        const built = results.filter((r) => r?.populated);
        const skipped = results.filter((r) => r?.populated === false);

        await load();

        if (built.length === 0 && skipped.length > 0) {
          enqueueSnackbar(
            "Nothing rebuilt. The sheet already has line items and was left alone.",
            { variant: "warning" },
          );
        } else if (built.length === 0 && results.length === 0) {
          // No plan at all: no budget or feasibility extract on the loan yet.
          enqueueSnackbar(
            "No budget or feasibility documents found to build from.",
            { variant: "warning" },
          );
        } else {
          const count = built.reduce((a, r) => a + (r.lineItemCount || 0), 0);
          const source =
            built[0]?.provenance?.source === "feasibility_review"
              ? "Feasibility Report"
              : "the borrower budget";
          enqueueSnackbar(
            `Rebuilt ${count} line ${count === 1 ? "item" : "items"} from ${source}`,
            { variant: "success" },
          );
        }
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail ||
            err?.message ||
            "Could not rebuild from documents.",
          { variant: "error" },
        );
      } finally {
        setSavingSheet(false);
      }
    },
    [api, dealId, enqueueSnackbar, load],
  );

  /**
   * FINAL budget (budget-scope-of-work-draws.md, 2026-09-22). PUT .../lock with
   * `locked` true / false; the sheet's current `budgetSource` rides along
   * unchanged. 422 when locking a sheet with no line items.
   */
  const handleLock = useCallback(
    async (subjectPropertyId, locked) => {
      if (!dealId) return;
      setSavingSheet(true);
      try {
        const res = await api.lockBudget(dealId, {
          subjectPropertyId: subjectPropertyId ? String(subjectPropertyId) : undefined,
          locked,
          budgetSource: null,
        });
        replaceSheet(res?.scope);
        enqueueSnackbar(
          locked ? "Locked as the final budget" : "Budget unlocked",
          { variant: "success" },
        );
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail || err?.message || "Could not change the lock.",
          { variant: "error" },
        );
      } finally {
        setSavingSheet(false);
      }
    },
    [api, dealId, enqueueSnackbar, replaceSheet],
  );

  /**
   * The reviewer's source choice. Recorded through the same lock route with
   * `locked: false` (the sheet is unlocked whenever its source can change).
   * "Rebuild from this source" re-runs the document build first — the build
   * itself picks feasibility over budget, so the sheet's lines come from the
   * loan's documents and the choice records which one is the budget of record.
   */
  const handleSetSource = useCallback(
    async (subjectPropertyId, source, { rebuild } = {}) => {
      if (!dealId || !source) return;
      if (rebuild) await handleRebuild(true);
      setSavingSheet(true);
      try {
        const {
          selectedBy, // eslint-disable-line no-unused-vars
          selectedAt, // eslint-disable-line no-unused-vars
          ...budgetSource
        } = source;
        const res = await api.lockBudget(dealId, {
          subjectPropertyId: subjectPropertyId ? String(subjectPropertyId) : undefined,
          locked: false,
          budgetSource,
        });
        replaceSheet(res?.scope);
        enqueueSnackbar("Budget source recorded", { variant: "success" });
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail || err?.message || "Could not record the source.",
          { variant: "error" },
        );
      } finally {
        setSavingSheet(false);
      }
    },
    [api, dealId, enqueueSnackbar, replaceSheet, handleRebuild],
  );

  /**
   * Every photo that evidences a budget line, keyed by `lineItemIds` and OLDEST
   * FIRST, so the workbook's per-line camera opens the line's progress over the
   * life of the loan. Matching is by id, never by the printed line name.
   */
  const photoLineMap = useMemo(() => photosByLineItem(reviews), [reviews]);

  /** A photo / evidence chip asking to see its budget row. */
  const handleOpenLine = useCallback((lineItemId) => {
    if (lineItemId) setFocusLineId(String(lineItemId));
  }, []);

  /**
   * "Open report at page N". The review knows the loan document it came from
   * (`sourceDocumentId`); the preview sidebar takes the whole document, and has
   * no page anchor, so this opens the file and the lightbox states the page.
   */
  const handlePreviewReport = useCallback(
    (review) => {
      const id = review?.sourceDocumentId && String(review.sourceDocumentId);
      const doc = (loan?.loanDocuments || []).find(
        (d) => String(d?._id) === id,
      );
      if (doc) dispatch(filePreviewSetData(doc));
    },
    [loan?.loanDocuments, dispatch],
  );

  /* ── section strip: scroll + spy + deep link ──────────────────────────── */
  // One page of stacked cards, so the sub-views are anchors. Mirrors the
  // Contacts tab exactly (see budgetDrawsSections.js).

  const hasInspections = useMemo(
    () => (reviews || []).some((r) => r?.reportKind !== "feasibility"),
    [reviews],
  );
  const sections = useMemo(
    () => getBudgetDrawsSections({ hasInspections }),
    [hasInspections],
  );

  const [activeSection, setActiveSection] = useState(
    BUDGET_SECTIONS.FEASIBILITY,
  );
  // While a programmatic scroll is running the observer would fight it, so the
  // strip holds the chosen section until the scroll settles.
  const jumpingTo = useRef(null);

  const jumpToSection = useCallback((key) => {
    const el = document.getElementById(budgetSectionAnchor(key));
    if (!el) return;
    jumpingTo.current = key;
    setActiveSection(key);
    el.scrollIntoView({ block: "start", behavior: "smooth" });
    window.setTimeout(() => {
      if (jumpingTo.current === key) jumpingTo.current = null;
    }, 700);
  }, []);

  // Scroll spy. `root: null` watches the viewport, which is the right answer
  // whatever element actually scrolls; the top band keeps "active" meaning
  // "the section under the strip".
  useEffect(() => {
    const nodes = sections
      .map((s) => document.getElementById(budgetSectionAnchor(s.key)))
      .filter(Boolean);
    if (!nodes.length || !("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (jumpingTo.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (!visible.length) return;
        const id = visible[0].target.id;
        const hit = sections.find((s) => budgetSectionAnchor(s.key) === id);
        if (hit) setActiveSection(hit.key);
      },
      { rootMargin: "-80px 0px -55% 0px", threshold: 0 },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [sections, loading]);

  // Deep link from the nav flyout. Consumed once and stripped, so the URL never
  // pins a stale section.
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get(BUDGET_SECTION_PARAM);
  useEffect(() => {
    if (!sectionParam || loading) return;
    if (sections.some((s) => s.key === sectionParam)) {
      // One frame, so the sections are mounted before we measure them.
      window.requestAnimationFrame(() => jumpToSection(sectionParam));
    }
    const next = new URLSearchParams(searchParams);
    next.delete(BUDGET_SECTION_PARAM);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam, loading]);

  // Tab visibility (construction products only) — not to be confused with
  // `eligible` below, which is the releasable-advance figure.
  const tabEligible =
    sow?.budgetTabEligible ?? drawsData?.budgetTabEligible ?? true;

  // `weighted` mirrors ScopeOfWork.totalWeightedAveragePercentComplete over the
  // active sheet's live rows (weighted by line cost) and seeds the draws strip
  // before any draw exists.
  const weightedPercentComplete = useMemo(() => {
    const total = sheetRows.reduce(
      (a, r) => a + num(r.estimatedTotalLineItemCost),
      0,
    );
    const weightedSum = sheetRows.reduce(
      (a, r) => a + num(r.percentComplete) * num(r.estimatedTotalLineItemCost),
      0,
    );
    return total > 0 ? weightedSum / total : 0;
  }, [sheetRows]);

  // The construction-capital strip sums ONLY locked (FINAL) sheets — an
  // unlocked sheet is a draft, not the budget of record — and says how many of
  // the loan's sheets are final (budget-scope-of-work-draws.md, 2026-09-22).
  const budgetFinal = useMemo(() => {
    const scopes = sow?.scopes || [];
    const lockedSheets = scopes.filter((s) => s?.budgetLocked);
    const lockedTotal = lockedSheets.reduce(
      (a, s) =>
        a +
        (s?.lineItems || []).reduce(
          (b, li) => b + num(li?.estimatedTotalLineItemCost),
          0,
        ),
      0,
    );
    return {
      finalCount: lockedSheets.length,
      sheetCount: scopes.length,
      lockedTotal,
    };
  }, [sow]);

  // Computed once, rendered twice (capital band + draws strip) so the two can
  // never quote different numbers for the same question.
  const eligible = useMemo(
    () =>
      buildEligible({
        draws: drawsData?.draws,
        weightedPercentComplete,
        rollup: drawsData?.rollup,
      }),
    [drawsData, weightedPercentComplete],
  );

  /**
   * Delete a draw (2026-10-02). Only the LAST draw — the backend 409s
   * otherwise. The draw is archived, so it can be restored; the response
   * carries the recomputed rollup, and the tab refetches to reseed the ledger.
   */
  const handleDeleteDraw = useCallback(
    async (draw) => {
      const id = draw?._id || draw?.oid;
      if (!dealId || !id) return;
      try {
        await api.deleteDraw(dealId, id);
        enqueueSnackbar(`Draw #${draw.drawNumber} deleted`, { variant: "success" });
        await load();
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail || err?.message || "Could not delete the draw.",
          { variant: "error" },
        );
      }
    },
    [api, dealId, enqueueSnackbar, load],
  );

  // Import site inspection: upload → the listener parses → the Inspections
  // panel (keyed on this) shows the report as "Parsing…" and polls.
  const [importing, setImporting] = useState(false);
  const [inspectionsKey, setInspectionsKey] = useState(0);
  const handleImportInspection = useCallback(
    async (files, drawId) => {
      if (!dealId || !files?.length) return;
      setImporting(true);
      try {
        await api.importInspections(dealId, files, drawId);
        enqueueSnackbar(
          `${files.length} inspection report${files.length === 1 ? "" : "s"} uploaded — reading now`,
          { variant: "success" },
        );
        setInspectionsKey((k) => k + 1);
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail || err?.message || "Could not upload the report.",
          { variant: "error" },
        );
      } finally {
        setImporting(false);
      }
    },
    [api, dealId, enqueueSnackbar],
  );

  /** Opens the ADD_DRAW sidebar — the tab's only new-draw entry point. */
  const handleNewDraw = useCallback(() => {
    // `{}` for state (not null) — the reducer keeps the prior state on null.
    dispatch(
      sidebarOpen(
        loanDrilldownSidebarTypes.ADD_DRAW,
        {},
        {
          onCreated: load,
          remainingHoldback: drawsData?.rollup?.remainingHoldback,
          totalHoldback: drawsData?.rollup?.totalHoldback,
          totalDrawnToDate: drawsData?.rollup?.totalDrawnToDate,
          // Hides the Day 1 option once an inspection-backed draw exists (the
          // server counts seller draws too, so this does as well).
          hasInspectedDraw: (drawsData?.draws || []).some(isInspectionBackedDraw),
        },
      ),
    );
  }, [dispatch, load, drawsData]);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      {/* The strip is pinned so the jump list stays reachable from anywhere on
          a long page — the same strip Contacts uses, just not scrolled away. */}
      {!loading && tabEligible && (
        <Box sx={{ position: "sticky", top: 0, zIndex: 3 }}>
          <DrilldownSubTabStrip
            options={sections.map((s) => ({
              key: s.key,
              label: s.label,
              icon: s.icon,
            }))}
            value={activeSection}
            onChange={jumpToSection}
          />
        </Box>
      )}
      {/* LoanTabCanvas carries the drilldown's standard page geometry —
          Container maxWidth "xl" (1536px) plus the shared 36px/8px insets — so
          this tab lines up with Properties, Applicants, AI Review and the rest
          instead of capping at its own width. See shared/tabLayout. */}
      <LoanTabCanvas>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {loading ? (
            <Flx ac jc sx={{ py: 8 }}>
              <CircularProgress size={24} />
            </Flx>
          ) : !tabEligible ? (
            <Flx ac jc sx={{ py: 8 }}>
              <Txt sx={{ fontSize: 12, color: T.faint }}>
                Budget &amp; Draws applies to construction loans only.
              </Txt>
            </Flx>
          ) : (
            <>
              {/* Required · ordered · on file · extracted · reviewed, the
                  report's own facts, and the per-property criteria. */}
              <Box
                id={budgetSectionAnchor(BUDGET_SECTIONS.FEASIBILITY)}
                sx={BUDGET_SECTION_ANCHOR_SX}
              >
                <FeasibilitySection
                  dealId={dealId}
                  reviews={reviews}
                  onOpenLine={handleOpenLine}
                  onPreviewReport={handlePreviewReport}
                />
              </Box>
              <Box
                id={budgetSectionAnchor(BUDGET_SECTIONS.CAPITAL)}
                sx={BUDGET_SECTION_ANCHOR_SX}
              >
                <CapitalOverviewCard
                  rollup={drawsData?.rollup}
                  drawCount={drawsData?.draws?.length || 0}
                  budgetTotal={budgetFinal.lockedTotal}
                  budgetFinal={budgetFinal}
                  eligible={eligible}
                  onNewDraw={handleNewDraw}
                />
              </Box>
              <Box
                id={budgetSectionAnchor(BUDGET_SECTIONS.SCOPE_OF_WORK)}
                sx={BUDGET_SECTION_ANCHOR_SX}
              >
                <ScopeOfWorkWorkbook
                  scopes={sow?.scopes}
                  properties={properties}
                  loading={loading}
                  saving={savingSheet}
                  onSave={handleSaveSheet}
                  onRebuild={handleRebuild}
                  onRowsChange={setSheetRows}
                  photoLineMap={photoLineMap}
                  focusLineId={focusLineId}
                  onFocusHandled={() => setFocusLineId(null)}
                  onPreviewReport={handlePreviewReport}
                  api={api}
                  dealId={dealId}
                  onLock={handleLock}
                  onSetSource={handleSetSource}
                  // Servicing-phase default: once the loan is drawing, the budget
                  // folds away so the ledger is what's on screen.
                  defaultCollapsed={(drawsData?.draws?.length || 0) > 0}
                />
              </Box>
              <Box
                id={budgetSectionAnchor(BUDGET_SECTIONS.DRAWS)}
                sx={BUDGET_SECTION_ANCHOR_SX}
              >
                <DrawsSection
                  dealId={dealId}
                  draws={drawsData?.draws}
                  rollup={drawsData?.rollup}
                  eligible={eligible}
                  onNewDraw={handleNewDraw}
                  fetchFciPayload={api.getDrawFciPayload}
                  onDeleteDraw={handleDeleteDraw}
                  onImportInspection={handleImportInspection}
                  importing={importing}
                />
                {/* Imported site-inspection reports: review, correct, apply. */}
                <InspectionsPanel
                  api={api}
                  dealId={dealId}
                  draws={drawsData?.draws}
                  properties={properties}
                  refreshKey={inspectionsKey}
                  onApplied={load}
                />
              </Box>
              {/* Site inspections sit after the ledger they evidence. */}
              <Box
                id={budgetSectionAnchor(BUDGET_SECTIONS.INSPECTIONS)}
                sx={BUDGET_SECTION_ANCHOR_SX}
              >
                <InspectionPhotosSection
                  api={api}
                  dealId={dealId}
                  reviews={reviews}
                  properties={properties}
                  onOpenLine={handleOpenLine}
                  onPreviewReport={handlePreviewReport}
                />
              </Box>
            </>
          )}
        </Box>
      </LoanTabCanvas>
    </LoanDrilldownTabPanel>
  );
};

export default BudgetDrawsTab;
